<template>
  <div class="km__modules__text">
    <TextDefault
        :show_textlist_button="showTextListButton"
        @openTextList="openTextList"
        @createTextObject="createTextObject"
    />
    <TextEdit
        :currentSelOb="textObjectToEdit"
        @closeOneLevel="closeOneLevel"
        @deleteTextElem="deleteTextElem"
        @endTextEditing="endTextEditing"
    />
    <TextList
        :textList="textObjects"
        @deleteTextElem="deleteTextElem"
        @endTextEditing="endTextEditing"
        @closeModule="closeOneLevel"
    />
    <TextTypeface
        :currentSelOb="textObjectToEdit"
    />
    <ColorShadeGroupSelection
        containerclass="km__modules__text--color"
        :currentColorValueIntOrNONEstring="getColorValue"
        :palettenCluster="currentPalettenClusterSorted"
        :animationkeyframesgetter="colorAnimation"
        @setColor="setFontColor"
        :currentpalette="currentpalette"
    />
    <ModuleColorShade
        containerclass="km__modules__text--color--shade"
        :currentColorValueIntOrNONEstring="getColorValue"
        :palettenCluster="currentPalettenClusterSorted"
        :animationkeyframesgetter="colorShadeAnimation"
        @setColor="setFontColor"
        :currentpalette="currentpalette"
    />
    <ColorShadeGroupSelection
        containerclass="km__modules__text--outline"
        hasNoColorOption="true"
        :currentColorValueIntOrNONEstring="getOutlineColorValue"
        :palettenCluster="currentPalettenClusterSorted"
        :animationkeyframesgetter="colorOutlineAnimation"
        @setColor="setOutlineColor"
        @setColorNone="setColorNone"
        :currentpalette="currentpalette"
    />
    <ModuleColorShade
        containerclass="km__modules__text--outline--shade"
        hasNoColorOption="true"
        :currentColorValueIntOrNONEstring="getOutlineColorValue"
        :palettenCluster="currentPalettenClusterSorted"
        :animationkeyframesgetter="colorOutlineShadeAnimation"
        @setColor="setOutlineColor"
        @setColorNone="setColorNone"
        :currentpalette="currentpalette"
    />
  </div>
</template>

<script lang="ts" setup>
import TextDefault from "./TextDefault.vue";
import TextEdit from "./TextEdit.vue";
import TextList from "./TextList.vue";
import TextTypeface from "./TextTypeface.vue";
import ModuleColorShade from "@km/components/color/ColorShadeSelection.vue";
import {computed, Ref, ref, watch} from "vue";
import {useStore} from "vuex";
import {TextObject} from "@k/js/selectableObjects";
import {ModuleTextFunctions} from "@km/components/text/ModuleTextFunctions";
import {SELOBEVENTNAMES} from "@k/global/GlobalEventnames";
import {TabNames} from "@km/data/tabs";
import {EventListener, EventVerteiler} from "@k/global/eventVerteiler";
import {
  getColorMainAnimation, getTextOutlineColorAnimation, getTextOutlineColorShadeAnimation,
  getTextColorShadeAnimation,
  TEXTMODULE_POSSIBLE_STATES
} from "@km/components/text/ModuleTextAnimations";
import {getInsertionTarget} from "@km/mixins/SelObInsertionPointFinder";
import {SelObTypes} from "@k/js/SelObTypes";
import ColorShadeGroupSelection from "@km/components/color/ColorShadeGroupSelection.vue";
import {CONSTANTS} from "@k/js/meta_data";
import globalsObject, {trackPageview} from "@k/js/globalsObject";
import {useModuleTextOpenStatusStore} from "@km/store/frontendstate/useModuleTextOpenState";
import {LOOPTRACKINGEVENTNAMES} from "@k/global/loopTracker";

function sendCreateTextItemEvent() {
  const target = getInsertionTarget();
  trackPageview("konfitrack-texthinzugefuegt.htm");
  globalsObject.loopTracker.trackBasicEvent(LOOPTRACKINGEVENTNAMES.KONFI_ADD_TEXT);
  EventVerteiler.broadcast(
      "createTextObjectFromFrontEndEvent",
      {
        textvalue: "text",
        isFixeValue: false,
        part: target.part,
        position: target.position
      }
  );
}


const store = useStore();

store.dispatch("loadPalettenClusters", ["CM5 Bedruckbar"]);

const openStatusStore = useModuleTextOpenStatusStore();
const eventListener = new EventListener();
const textTabFinishedLoading = ref(false);
const textObjectToEdit = ref(null) as Ref<TextObject | null>;
const functions = new ModuleTextFunctions(store, textObjectToEdit);
functions.setEventListeners(eventListener, store);

watch(() => store.getters.TEXTTABisOpen, (isOpen: boolean) => {
  isOpen ? openStatusStore.ensureStateIsNotClosed() : openStatusStore.close();
});
watch(() => openStatusStore.getCurrentState, (state: TEXTMODULE_POSSIBLE_STATES) => {
  if (([TEXTMODULE_POSSIBLE_STATES.closed, TEXTMODULE_POSSIBLE_STATES.textverwaltung] as TEXTMODULE_POSSIBLE_STATES[]).indexOf(state) > -1) {
    functions.deselectIfSelected();
  }
});
watch(() => store.getters["FontStore/getFlattenedFontGroups"], fonts => textTabFinishedLoading.value = fonts.length > 0)
// TEXT_TAB_HAS_FINISHED_LOADING
eventListener.addEventListener(SELOBEVENTNAMES.SELECTED, (data) => {
  if (data.type === SelObTypes.TEXT) {
    openStatusStore.setCurrentState( TEXTMODULE_POSSIBLE_STATES.edit);
    if (!store.getters.TEXTTABisOpen) {
      store.commit("SET_TAB_OPEN", TabNames.TEXT);
    }
  }
});
const textTabNotReady = () => console.log("texttab not ready yet");

const createTextObject = () => {
  if (!textTabFinishedLoading.value) {
    textTabNotReady();
    return;
  }
  sendCreateTextItemEvent();
};

const {
  currentPalettenClusterSorted,
  getColorValue,
  getOutlineColorValue,
  textObjects
} = functions.getComputedValues(textObjectToEdit, store);

const closeOneLevel = () => {
  openStatusStore.goUpOneLevel();
}


const deleteTextElem = (id) => functions.deleteTextElem(id);
const endTextEditing = () => EventVerteiler.broadcast(SELOBEVENTNAMES.SELECTION_CLEARED);
const setFontColor = (colorObj) => functions.setFontColor(colorObj);
const setOutlineColor = (colorObj) => functions.setOutlineColor(colorObj);
const setColorNone = () => functions.setColorNone();
const openTextList = () => openStatusStore.setCurrentState(TEXTMODULE_POSSIBLE_STATES.textlist);
const colorAnimation = getColorMainAnimation();
const colorShadeAnimation = getTextColorShadeAnimation();
const colorOutlineAnimation = getTextOutlineColorAnimation();
const colorOutlineShadeAnimation = getTextOutlineColorShadeAnimation();
const showTextListButton = computed(() => textObjects.value.length > 0);
const currentpalette = CONSTANTS.STANDARD_PALETTE;

</script>

<style lang="scss" scoped>
.maincolorpickercomponent {
  background: white;
}
</style>
